import CustomTooltip from '../../shared/CustomTooltip';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Grid,
    List,
    ListItem,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAddIndividualBookingCapMutation,
    useDeleteBookingCapMutation,
    useEditBookingCapMutation,
    useGetAllAssetsQuery,
    useGetAllGuestsQuery,
    useGetBookingCapListQuery,
    useGetIndividualGuestsQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import { CustomTextField } from '../../../layout/styledcomponents/CustomTextField';
import DropdownFilter from '../../../layout/styledcomponents/filters/Dropdown';
import MultiSelectDropdownFilter from '../../../layout/styledcomponents/filters/MultiSelectDropdown';
import UserRole from '../../../shared/AuthRoles';
import { handleHttpError } from '../../../shared/utils';
import ConfirmationModal from '../../shared/ConfirmationModal';
import toast from '../../shared/Toast';
import { BookingCaps } from './BookingCapList';

interface Props {
    capProp?: BookingCaps;
}

const BookingCapForm = ({ capProp }: Props) => {
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const isGuest = loggedInUser?.role?.includes('guest');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');

    const theme = useTheme();

    const { data: allGuests } = useGetAllGuestsQuery(`/guests`);
    // const [guestList, setGuestList] = useState<any>([]);
    const [assetList, setAssetList] = useState<any>([]);
    const [isDuplicateName, setDuplicateName] = useState(false);
    const [addNewBookingCap] = useAddIndividualBookingCapMutation();
    const [bookingCapName, setBookingCapName] = useState('');
    const [yearlyCap, setYearlyCap] = useState<any>(null);
    const [monthlyCap, setMonthlyCap] = useState<any>(null);
    const [weeklyCap, setWeeklyCap] = useState<any>(null);
    const [experienceTypes, setExperienceTypes] = useState<string[]>([]);
    const [experiences, setExperience] = useState<any>([]);
    const [visitType, setVisitType] = useState(null);
    const [guests, setGuests] = useState<string[]>([]);
    const [excludedUsers, setExcludedUsers] = useState<any>([]);
    const [excludedUserList, setExcludedUserList] = useState<any>([]);
    const [appliesTo, setAppliesTo] = useState('');
    const [excludeCompany, setExcludeCompany] = useState<any>();

    const companyId = isOwner ? excludeCompany?.id : loggedInUser?.company;
    const { data: individualGuests } = useGetIndividualGuestsQuery(
        `/guests/${companyId}/users`,
    );

    const navigate = useNavigate();
    const [editBookingCap] = useEditBookingCapMutation();
    const [deleteBookingCap] = useDeleteBookingCapMutation();
    const { data: getBookingCaps } = useGetBookingCapListQuery('/booking-caps');
    const [showModal, setShowModal] = useState(false);

    const { data: allAssetsForOwner } = useGetIndividualOwnerAssetsQuery(
        {
            url: `owners/${loggedInUser?.company}/modules`,
            status: 'published',
            sort: 'name',
        },
        { skip: !isOwner || !loggedInUser?.company },
    );
    const { data: allAssetsForGuest, isFetching } = useGetAllAssetsQuery(
        {
            url: '/modules',
            sort: 'name',
        },
        { skip: !isGuest },
    );

    const allAssets = loggedInUser?.role?.includes('owner')
        ? allAssetsForOwner
        : loggedInUser?.role?.includes('guest')
          ? allAssetsForGuest
          : [];

    const bookingCapNames = getBookingCaps?.map(
        (cap: BookingCaps) => cap?.name,
    );
    const handlebookingCapName = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const findDuplicate = bookingCapNames?.find(
            (bookingCapName: string) => bookingCapName === event?.target?.value,
        );
        if (
            findDuplicate !== undefined &&
            findDuplicate !== getBookingCaps?.caps?.name
        ) {
            setDuplicateName(true);
            setBookingCapName('');
        } else {
            setDuplicateName(false);
            setBookingCapName(event.target?.value?.toLowerCase());
        }
    };

    const handleYearlyCap = (event: any) => {
        const newAmount = Number(event.target.value);
        if (newAmount === 0) {
            setYearlyCap(undefined);
        } else {
            setYearlyCap(newAmount);
        }
    };

    const handleMonthlyCap = (event: any) => {
        const newAmount = Number(event.target.value);
        if (newAmount === 0) {
            setMonthlyCap(undefined);
        } else {
            setMonthlyCap(newAmount);
        }
    };
    const handleWeeklyCap = (event: any) => {
        const newAmount = Number(event.target.value);
        if (newAmount === 0) {
            setWeeklyCap(undefined);
        } else {
            setWeeklyCap(newAmount);
        }
    };

    const errors: any = [];

    const noCapSet =
        (yearlyCap === null || yearlyCap === undefined) &&
        (monthlyCap === null || monthlyCap === undefined) &&
        (weeklyCap === null || weeklyCap === undefined);

    if (bookingCapName?.length === 0) {
        errors.push('Name cannot be empty');
    }

    if (noCapSet) {
        errors.push('At least one cap must be set');
    }
    if (experiences?.length === 0) {
        errors.push('Experiences cannot be empty');
    }
    if (experienceTypes?.length === 0) {
        errors.push('Experience Types cannot be empty');
    }
    if (guests?.length === 0 && isOwner) {
        errors.push('Guests cannot be empty');
    }
    if (appliesTo === '' || appliesTo === null || appliesTo === undefined) {
        errors.push('Applies to cannot be empty');
    }

    useEffect(() => {
        if (capProp && capProp?.name) {
            setBookingCapName(capProp?.name);
            setYearlyCap(capProp?.yearlyCap);
            setMonthlyCap(capProp?.monthlyCap);
            setWeeklyCap(capProp?.weeklyCap);
            setExperienceTypes(capProp?.moduleTypes);
            setExperience(capProp?.modules?.map((mod: any) => mod?.id));
            if (capProp?.moduleTypes && allAssets !== undefined) {
                let list = [];
                if (capProp?.moduleTypes?.length === 1) {
                    const oneVal = allAssets
                        ?.flat()
                        ?.filter((experience: any) => {
                            return experience?.type.includes(
                                capProp?.moduleTypes,
                            );
                        });
                    list = oneVal;
                } else if (capProp?.moduleTypes?.length === 2) {
                    const twoVal = allAssets
                        ?.flat()
                        ?.filter((experience: any) => {
                            return (
                                experience?.type.includes(
                                    capProp?.moduleTypes[0],
                                ) ||
                                experience?.type.includes(
                                    capProp?.moduleTypes[1],
                                )
                            );
                        });
                    list = twoVal;
                } else {
                    const threeVal = allAssets
                        ?.flat()
                        ?.filter((experience: any) => {
                            return (
                                experience?.type.includes(
                                    capProp?.moduleTypes[0],
                                ) ||
                                experience?.type.includes(
                                    capProp?.moduleTypes[1],
                                ) ||
                                experience?.type.includes(
                                    capProp?.moduleTypes[2],
                                )
                            );
                        });
                    list = threeVal;
                }

                handleAssetList(list);
            }
            setVisitType(null);
            setAppliesTo(capProp?.appliesTo);

            setGuests(capProp?.guests?.map((guest: any) => guest?.id));

            const excludedUsersCapProp = capProp?.excludedUsers?.map(
                (guest: any) => {
                    return {
                        value: guest?.id,
                        label: `${guest?.firstName} ${guest?.lastName} - ${
                            guest?.email
                                ? `${guest?.email} (${guest?.companyName})`
                                : ''
                        }`,
                    };
                },
            );
            setExcludedUserList(excludedUsersCapProp);
            setExcludedUsers(
                capProp?.excludedUsers?.map((guest: any) => guest?.id),
            );
        }
    }, [capProp, allAssets]);

    const handleExperienceType = (value: any) => {
        setExperienceTypes(value);
        let list = [];
        if (experiences?.length !== 0) {
            removeExperienceType(value);
            if (value?.length === 1) {
                const oneVal = allAssets?.flat()?.filter((experience: any) => {
                    return experience?.type.includes(value);
                });
                list = oneVal;
            } else if (value?.length === 2) {
                const twoVal = allAssets?.flat()?.filter((experience: any) => {
                    return (
                        experience?.type.includes(value[0]) ||
                        experience?.type.includes(value[1])
                    );
                });
                list = twoVal;
            } else {
                const threeVal = allAssets
                    ?.flat()
                    ?.filter((experience: any) => {
                        return (
                            experience?.type.includes(value[0]) ||
                            experience?.type.includes(value[1]) ||
                            experience?.type.includes(value[2])
                        );
                    });
                list = threeVal;
            }
        } else {
            if (value?.length === 1) {
                const oneVal = allAssets?.flat()?.filter((experience: any) => {
                    return experience?.type.includes(value);
                });
                list = oneVal;
            } else if (value?.length === 2) {
                const twoVal = allAssets?.flat()?.filter((experience: any) => {
                    return (
                        experience?.type.includes(value[0]) ||
                        experience?.type.includes(value[1])
                    );
                });
                list = twoVal;
            } else {
                const threeVal = allAssets
                    ?.flat()
                    ?.filter((experience: any) => {
                        return (
                            experience?.type.includes(value[0]) ||
                            experience?.type.includes(value[1]) ||
                            experience?.type.includes(value[2])
                        );
                    });
                list = threeVal;
            }
        }

        handleAssetList(list);
    };

    const handleAssetList = (list: any) => {
        const assetList = list?.map((asset: any) => ({
            value: asset.id,
            label: asset.name,
        }));
        setAssetList(assetList);
    };

    const removeExperienceType = (value: any) => {
        let experiencesToUncheck: any = [];
        if (value?.length === 1) {
            const oneVal = allAssets?.filter((experience: any) => {
                return !experience?.type.includes(value);
            });
            experiencesToUncheck = oneVal;
        } else if (value?.length === 2) {
            const twoVal = allAssets?.filter((experience: any) => {
                return (
                    !experience?.type.includes(value[0]) &&
                    !experience?.type.includes(value[1])
                );
            });
            experiencesToUncheck = twoVal;
        } else {
            return;
        }

        const experienceIds = experiencesToUncheck?.map((i: any) => i.id);

        //remove elements from experiences array that do not match the unselected experience type
        let elements = experiences;
        elements = elements.filter(function (el: any) {
            return experienceIds.indexOf(el) < 0;
        });

        setExperience(elements);
    };

    const handleRemoveExcludedUser = (value: any) => {
        setExcludedUsers(excludedUsers?.filter((user: any) => user !== value));

        setExcludedUserList(
            excludedUserList?.filter((user: any) => user?.value !== value),
        );
    };

    const saveBookingCap = () => {
        if (capProp) {
            editBookingCap([
                capProp.id,
                {
                    name: bookingCapName,
                    yearlyCap: yearlyCap ?? null,
                    monthlyCap: monthlyCap ?? null,
                    weeklyCap: weeklyCap ?? null,
                    moduleTypes: experienceTypes,
                    modules: experiences?.map((mod: any) => ({
                        id: mod,
                    })),
                    visitType: visitType,
                    ...(isOwner && {
                        guests: guests?.map((guest: any) => ({
                            id: guest,
                        })),
                    }),
                    excludedUsers: excludedUsers?.map((guest: any) => ({
                        id: guest,
                    })),
                    appliesTo: appliesTo,
                },
            ]).then((data: any) => {
                if (data?.error) {
                    const errorMessage = handleHttpError(data?.error);
                    toast.error(errorMessage);
                } else {
                    toast.success(
                        `Booking Cap: ${bookingCapName} has been updated`,
                    );
                    navigate('/bookingCaps');
                }
            });
        } else {
            if (
                ['owner_company', 'owner_admin', 'guest_admin'].includes(
                    loggedInUser?.role,
                )
            ) {
                addNewBookingCap({
                    name: bookingCapName,
                    yearlyCap: yearlyCap ?? null,
                    monthlyCap: monthlyCap ?? null,
                    weeklyCap: weeklyCap ?? null,
                    moduleTypes: experienceTypes,
                    modules: experiences?.map((mod: any) => ({
                        id: mod,
                    })),
                    visitType: visitType,
                    guests: isOwner
                        ? guests?.map((guest: any) => ({
                              id: guest,
                          }))
                        : [],
                    excludedUsers: excludedUsers?.map((guest: any) => ({
                        id: guest,
                    })),
                    appliesTo: appliesTo,
                }).then((data: any) => {
                    if (data?.error) {
                        const message = handleHttpError(data?.error);
                        toast.error(message);
                    } else {
                        toast.success(
                            `New booking cap: ${bookingCapName} has been created`,
                        );
                        navigate('/bookingCaps');
                    }
                });
            } else {
                toast.error(
                    `You don't have the necessary rights to add booking caps`,
                );
            }
        }
    };

    const handleDeleteCap = () => {
        deleteBookingCap([capProp?.id]).then((data: any) => {
            if (data.error) {
                toast.error(`Booking Cap: ${bookingCapName} cannot be deleted`);
            } else {
                toast.success(
                    `Booking Cap: ${bookingCapName} has been deleted`,
                );
                navigate('/bookingCaps');
            }
        });
    };

    return (
        <Grid
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '40px',
            }}
        >
            <Grid
                sx={{
                    width: '100%',
                    justifyContent: 'left',
                    display: 'flex',
                }}
            >
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    {capProp ? `Edit Booking Cap` : ` New Booking Cap`}
                </Typography>
            </Grid>
            <Grid container spacing={2}>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}>
                            <Typography>
                                Name:{' '}
                                {bookingCapName === '' ? (
                                    <span
                                        style={{
                                            color: 'red',
                                        }}
                                    >
                                        *
                                    </span>
                                ) : null}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <CustomTextField
                                color={'green'}
                                error={isDuplicateName}
                                fullWidth={true}
                                label={
                                    isDuplicateName === true
                                        ? 'Error: Booking cap name already exists'
                                        : ''
                                }
                                value={!isDuplicateName ? bookingCapName : null}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={handlebookingCapName}
                                inputProps={{
                                    style: {
                                        height: '10px',
                                        width: '350px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{
                                paddingBottom: isMobile || isIpad ? '5px' : '0',
                                display: 'flex',
                                gap: '2px',
                            }}
                        >
                            {' '}
                            <Grid>
                                <Typography>
                                    Max No. Bookings per year:
                                </Typography>{' '}
                            </Grid>
                            <Grid>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Maximum bookings per user or per
                                            company can be applied to
                                            experiences. These caps are for the
                                            entire year.
                                            <List>
                                                For Example:
                                                <ListItem>
                                                    USER: Max 2 bookings per
                                                    user per year for each
                                                    experience you select. The
                                                    user will be able to book
                                                    max two bookings per
                                                    experience over the 12
                                                    months. This number will be
                                                    reset each calendar year.
                                                </ListItem>
                                                <ListItem>
                                                    COMPANY: Max 100 bookings
                                                    per company per year for
                                                    each experience you select.
                                                    The company will be able to
                                                    book max 100 bookings per
                                                    experience over the 12
                                                    months. This number will be
                                                    reset each calendar year.
                                                    This means users can make as
                                                    many bookings as they want
                                                    until they reach the
                                                    combined company total of
                                                    100.
                                                </ListItem>
                                                You can select whether this cap
                                                applies to each user or company
                                                wide below.
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            {' '}
                            <CustomTextField
                                color={'green'}
                                error={yearlyCap === 0 || noCapSet}
                                fullWidth={true}
                                label={
                                    yearlyCap === 0
                                        ? 'Error: Cap cannot be 0'
                                        : noCapSet
                                          ? 'Error: At least one cap must be set'
                                          : ''
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                type='number'
                                value={yearlyCap}
                                onChange={handleYearlyCap}
                                inputProps={{
                                    style: {
                                        height: '10px',
                                        width: '350px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{
                                paddingBottom: isMobile || isIpad ? '5px' : '0',
                                display: 'flex',
                                gap: '2px',
                            }}
                        >
                            {' '}
                            <Grid>
                                <Typography>
                                    Max No. Bookings per month:
                                </Typography>{' '}
                            </Grid>
                            <Grid></Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            {' '}
                            <CustomTextField
                                color={'green'}
                                error={monthlyCap === 0 || noCapSet}
                                fullWidth={true}
                                label={
                                    monthlyCap === 0
                                        ? 'Error: Cap cannot be 0'
                                        : noCapSet
                                          ? 'Error: At least one cap must be set'
                                          : ''
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                type='number'
                                value={monthlyCap}
                                onChange={handleMonthlyCap}
                                inputProps={{
                                    style: {
                                        height: '10px',
                                        width: '350px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{
                                paddingBottom: isMobile || isIpad ? '5px' : '0',
                                display: 'flex',
                                gap: '2px',
                            }}
                        >
                            {' '}
                            <Grid>
                                <Typography>
                                    Max No. Bookings per week:
                                </Typography>{' '}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            {' '}
                            <CustomTextField
                                color={'green'}
                                error={weeklyCap === 0 || noCapSet}
                                fullWidth={true}
                                label={
                                    weeklyCap === 0
                                        ? 'Error: Cap cannot be 0'
                                        : noCapSet
                                          ? 'Error: At least one cap must be set'
                                          : ''
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                type='number'
                                value={weeklyCap}
                                onChange={handleWeeklyCap}
                                inputProps={{
                                    style: {
                                        height: '10px',
                                        width: '350px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}>
                            <Typography
                                component={'h2'}
                                variant='h4'
                                sx={{
                                    textAlign: 'left',
                                    paddingBottom: '30px',
                                }}
                            >
                                Apply Cap To
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={7}></Grid>
                    </Grid>
                </Grid>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}>
                            <Typography>
                                Experience Type:
                                {experienceTypes?.length === 0 ? (
                                    <span
                                        style={{
                                            color: 'red',
                                        }}
                                    >
                                        *
                                    </span>
                                ) : null}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <MultiSelectDropdownFilter
                                id={'typeFilter'}
                                options={[
                                    {
                                        value: 'property',
                                        label: 'Property',
                                    },
                                    { value: 'boat', label: 'Boat' },
                                    {
                                        value: 'vehicle',
                                        label: 'Vehicle',
                                    },
                                    {
                                        value: 'activity',
                                        label: 'Activity',
                                    },
                                    {
                                        value: 'commercial',
                                        label: 'Commercial',
                                    },
                                ]}
                                handleChange={handleExperienceType}
                                selectedValue={experienceTypes}
                                backgroundColor={'white'}
                                border={theme.palette.midGrey.main}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Grid
                                item
                                xs={12}
                                lg={5}
                                sx={{
                                    paddingBottom:
                                        isMobile || isIpad ? '5px' : '0',
                                    display: 'flex',
                                    gap: '2px',
                                }}
                            >
                                {' '}
                                <Grid>
                                    <Typography>Experience:</Typography>{' '}
                                </Grid>
                                <Grid>
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                Select which experiences this
                                                cap applies to. Note that the
                                                cap applies to each individual
                                                experience. It is not an overall
                                                cap shared amoungst the selected
                                                experience.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon
                                            sx={{
                                                fontSize: '16px',
                                            }}
                                        />
                                    </CustomTooltip>
                                </Grid>
                                {experiences?.length === 0 ? (
                                    <span
                                        style={{
                                            color: 'red',
                                        }}
                                    >
                                        *
                                    </span>
                                ) : null}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <MultiSelectDropdownFilter
                                disabled={experienceTypes.length === 0}
                                id={'experienceFilter'}
                                options={assetList}
                                handleChange={setExperience}
                                selectedValue={experiences}
                                backgroundColor={theme.palette.neutral.main}
                                border={theme.palette.midGrey.main}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{
                                paddingBottom: isMobile || isIpad ? '5px' : '0',
                                display: 'flex',
                                gap: '2px',
                            }}
                        >
                            <Grid>
                                <Typography>Applies to:</Typography>{' '}
                            </Grid>
                            <Grid>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Select if the cap applies to the
                                            entire company and is shared
                                            amoungst all the users or if the cap
                                            applies to per person.
                                            <List>
                                                For Example:
                                                <ListItem>
                                                    USER: Max 2 bookings per
                                                    user per year for each
                                                    experience you select. The
                                                    user will be able to book
                                                    max two bookings per
                                                    experience over the 12
                                                    months. This number will be
                                                    reset each calendar year.
                                                </ListItem>
                                                <ListItem>
                                                    COMPANY: Max 100 bookings
                                                    per company per year for
                                                    each experience you select.
                                                    The compan will be able to
                                                    book max 100 bookings per
                                                    experience over the 12
                                                    months. This number will be
                                                    reset each calendar year.
                                                    This means users can make as
                                                    many bookings as they want
                                                    until they reach the
                                                    combined company total of
                                                    100.
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Grid>
                            {appliesTo?.length === 0 ? (
                                <span
                                    style={{
                                        color: 'red',
                                    }}
                                >
                                    *
                                </span>
                            ) : null}
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <DropdownFilter
                                id={'visitType'}
                                options={[
                                    { value: 'company', label: 'Company' },
                                    { value: 'user', label: 'User' },
                                ]}
                                handleChange={setAppliesTo}
                                selectedValue={appliesTo}
                                backgroundColor={theme.palette.neutral.main}
                                border={theme.palette.midGrey.main}
                            />
                        </Grid>
                    </Grid>
                    {isOwner && (
                        <Grid item container xs={12} md={6}>
                            {' '}
                            <Grid item xs={12} lg={5}>
                                <Grid
                                    item
                                    xs={12}
                                    lg={5}
                                    sx={{
                                        paddingBottom:
                                            isMobile || isIpad ? '5px' : '0',
                                        display: 'flex',
                                        gap: '2px',
                                    }}
                                >
                                    {' '}
                                    <Grid>
                                        <Typography>Guests:</Typography>{' '}
                                    </Grid>
                                    <Grid>
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    Select which guests to apply
                                                    the cap to. Note that if you
                                                    do not select a guest, they
                                                    will have unlimited
                                                    bookings.
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Grid>
                                    {guests?.length === 0 ? (
                                        <span
                                            style={{
                                                color: 'red',
                                            }}
                                        >
                                            *
                                        </span>
                                    ) : null}
                                </Grid>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <MultiSelectDropdownFilter
                                    id={'allGuestsFilter'}
                                    options={
                                        allGuests
                                            ? allGuests?.map((guest: any) => ({
                                                  value: guest.id,
                                                  label: guest.name,
                                              }))
                                            : []
                                    }
                                    handleChange={setGuests}
                                    selectedValue={guests}
                                    backgroundColor={theme.palette.neutral.main}
                                    border={theme.palette.midGrey.main}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}>
                            <Grid
                                item
                                xs={12}
                                lg={5}
                                sx={{
                                    paddingBottom:
                                        isMobile || isIpad ? '5px' : '0',
                                    display: 'flex',
                                    gap: '2px',
                                }}
                            >
                                {' '}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}></Grid>
                    </Grid>
                </Grid>

                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}>
                            <Grid
                                item
                                xs={12}
                                lg={10}
                                sx={{
                                    paddingBottom:
                                        isMobile || isIpad ? '5px' : '0',
                                    display: 'flex',
                                    gap: '2px',
                                }}
                            >
                                {' '}
                                <Grid>
                                    <Typography
                                        component={'h2'}
                                        variant='h4'
                                        sx={{
                                            textAlign: 'left',
                                            paddingBottom: '30px',
                                        }}
                                    >
                                        Exclude Users:
                                    </Typography>{' '}
                                </Grid>
                                <Grid>
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                {isOwner
                                                    ? `Exclude users such as VIPs from
                                                organsations. They will not have
                                                this cap applied to them when
                                                making a booking.`
                                                    : `Exclude users such as VIPs from. They will not have
                                                this cap applied to them when
                                                making a booking.`}
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon
                                            sx={{
                                                fontSize: '16px',
                                            }}
                                        />
                                    </CustomTooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7}></Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        {' '}
                        <Grid item xs={12} lg={5}></Grid>
                        <Grid item xs={12} lg={7}></Grid>
                    </Grid>
                </Grid>
                <Grid container item columnSpacing='50px' rowSpacing='10px'>
                    {isOwner && (
                        <Grid item container xs={12} md={6}>
                            {' '}
                            <Grid item xs={12} lg={5}>
                                <Typography>Company:</Typography>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <DropdownFilter
                                    id={'excludeGuestCompanyFilter'}
                                    options={
                                        allGuests
                                            ? allGuests?.map((guest: any) => ({
                                                  value: guest,
                                                  label: guest.name,
                                              }))
                                            : []
                                    }
                                    handleChange={setExcludeCompany}
                                    selectedValue={excludeCompany?.name}
                                    backgroundColor={theme.palette.neutral.main}
                                    border={theme.palette.midGrey.main}
                                />
                            </Grid>
                        </Grid>
                    )}
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Typography>Users:</Typography>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <MultiSelectDropdownFilter
                                id={'excludedUserList'}
                                disabled={
                                    excludeCompany === undefined && isOwner
                                }
                                options={individualGuests}
                                handleChange={setExcludedUsers}
                                selectedValue={excludedUsers}
                                backgroundColor={theme.palette.neutral.main}
                                border={theme.palette.midGrey.main}
                                handleObject={setExcludedUserList}
                                selectedObject={excludedUserList}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {excludedUserList.length !== 0 ? (
                    <Grid container item columnSpacing='50px' rowSpacing='10px'>
                        <Grid item container xs={12} md={8}>
                            {' '}
                            <Grid item xs={12} lg={3.7}></Grid>
                            <Grid item xs={12} lg={7}>
                                {excludedUserList.map((user: any) => (
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: isMobile
                                                    ? '12px'
                                                    : '16px',
                                            }}
                                        >
                                            <li>{user?.label}</li>
                                        </Typography>
                                        <Grid>
                                            <CustomIconButton
                                                onClick={() =>
                                                    handleRemoveExcludedUser(
                                                        user?.value,
                                                    )
                                                }
                                                sx={{
                                                    width: '100%',
                                                    height: '25px',
                                                    display: 'flex',
                                                    flexDirection: 'row',

                                                    '&:hover': {
                                                        cursor: 'pointer',
                                                        color: 'red',
                                                        backgroundColor:
                                                            'transparent',
                                                        background: 'none',
                                                    },
                                                }}
                                            >
                                                <Grid
                                                    sx={{
                                                        width: '10%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'left',
                                                    }}
                                                >
                                                    <ClearIcon
                                                        sx={{
                                                            fontSize: '12px',
                                                        }}
                                                    />
                                                </Grid>
                                            </CustomIconButton>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item container xs={2} md={2}>
                            {' '}
                            <Grid item xs={2} lg={2}></Grid>
                            <Grid item xs={2} lg={2}></Grid>
                        </Grid>
                    </Grid>
                ) : null}

                <Grid
                    container
                    item
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingBottom: '50px',
                    }}
                >
                    <Grid
                        sx={{
                            display: 'flex',
                            gap: '10px',
                        }}
                    >
                        {capProp && (
                            <CustomButton
                                id='deleteBookingCap'
                                variant='contained'
                                color='warning'
                                onClick={() => {
                                    setShowModal(!showModal);
                                }}
                            >
                                Delete
                            </CustomButton>
                        )}

                        <CustomButton
                            variant='contained'
                            color='primary'
                            onClick={saveBookingCap}
                            disabled={errors?.length > 0}
                        >
                            {capProp ? `Update` : ` Save`}
                        </CustomButton>
                    </Grid>

                    <ConfirmationModal
                        open={showModal}
                        onClose={() => setShowModal(false)}
                        onChange={handleDeleteCap}
                        message={'delete this booking cap'}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

export default BookingCapForm;
